import LoadingSpinner from '@components/shared/LoadingSpinner';
import { useSystemLanguage } from '@contexts/SystemLanguageContext';
import { SupportedEntitlements } from '@core/models/entitlements';
import { SelectedEntitiesModel } from '@cw-elements/mfe-setup/entities-hall/entities-hall.model';
import { loadRemoteModule } from '@cw-elements/mfe-setup/load';
import { EntitiesHallMFEProps } from '@cw-elements/mfe-setup/util';
import { useAnomaliesByOrg } from '@hooks/queries/anomalyDetection/useAnomaliesByOrg';
import { useOrganization } from '@hooks/queries/useOrganization';
import { useEntitlements } from '@hooks/useEntitlements';
import { useUnits } from '@hooks/useUnits';
import { hallOfEntitiesStore } from '@state/hallOfEntities/hallOfEntities.store';
import { createBrowserHistory } from 'history';
import { Suspense, lazy, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { farmAnomalyDataInformation } from './HallOfEntitiesMfe.utils';

export const browserHistory = createBrowserHistory();

const EntitiesHall = lazy(() => loadRemoteModule<EntitiesHallMFEProps>('entities-hall/index'));

export function HallOfEntitiesMfe() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { setSelectedEntities } = hallOfEntitiesStore();
  const { organization } = useOrganization();
  const [language] = useSystemLanguage();
  const entitlements = useEntitlements();
  const { longTranslationKey, area } = useUnits();
  const { data: anomaliesByOrg, isLoading } = useAnomaliesByOrg({
    org: organization,
  });
  const hasAnomalyDetectionEntitlement = entitlements.has(SupportedEntitlements.ANOMALY_DETECTION);

  const onSelectEntity = useCallback(
    (entity: SelectedEntitiesModel) => {
      setSelectedEntities(entity);
      if (entity.farm?.id && entity.organization?.id) {
        const url = `/app/organizations/${entity.organization.id}/properties/${entity.farm.id}`;
        navigate(url);
      }
    },
    [setSelectedEntities, navigate]
  );

  const farmAdditionalData = useCallback(
    (farmId: string) => {
      return farmAnomalyDataInformation(
        farmId,
        anomaliesByOrg,
        isLoading,
        longTranslationKey,
        area,
        t
      );
    },

    [isLoading, area, anomaliesByOrg, longTranslationKey, t]
  );

  return (
    <Suspense fallback={<LoadingSpinner title={t('hall.loading_mfe')} />}>
      <div className="h-full w-full overflow-y-auto">
        <EntitiesHall
          baseRoute="/app/organizations"
          userLanguage={language}
          onEntitySelection={onSelectEntity}
          defaultConfig={{
            seasonFilter: 'false',
          }}
          farmAdditionalData={hasAnomalyDetectionEntitlement ? farmAdditionalData : undefined}
        />
      </div>
    </Suspense>
  );
}
